import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';

import { InlineEditRedlineBubble } from './InlineEditRedlineBubble';
import { useOutsideAlerter } from './useClickOutside';
import { useExclusiveTranslationPopover } from './useExclusiveTranslationPopover';

export interface InlineTranslatedPhraseFieldProps {
  /** The phrase text the field opens with. */
  initialValue: string;
  /** Character offset the caret opens at (clamped). */
  caretOffset: number;
  /** The phrase span this field is rendered inside; the bubble anchors to it. */
  anchorElement: HTMLElement;
  /** Enter. Receives the raw draft; the host normalises and saves. */
  onCommit: (draft: string) => void;
  /** Escape, click outside, another editor opening. Nothing is saved. */
  onCancel: () => void;
  canRevert: boolean;
  onRevert?: () => void;
}

/**
 * PLAT-11650 — "click a translated phrase and type": the phrase's own text
 * becomes an editable inline box, in the same font, line box and position, so
 * nothing on the page shifts and the text wraps like the prose around it. It is
 * rendered as the CHILDREN of the phrase span, so the span's identity
 * (`data-initial-index`, `data-source-uuid`) and the flat sibling DOM the
 * selection code relies on are untouched.
 *
 * Uncontrolled `contentEditable`: React never renders children into it; the
 * draft is read back from `textContent` on input. Keys are trapped natively on
 * the element (the transcript scroll container stops keys before React sees
 * them, and document-level shortcuts must not fire while typing): Enter commits,
 * Escape cancels, everything else stops here. Click outside cancels — the only
 * save is Enter, and the bubble says so.
 */
export const InlineTranslatedPhraseField: React.FC<
  InlineTranslatedPhraseFieldProps
> = ({
  initialValue,
  caretOffset,
  anchorElement,
  onCommit,
  onCancel,
  canRevert,
  onRevert,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [draft, setDraft] = useState(initialValue);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const cancel = useCallback(() => onCancelRef.current(), []);
  // Click outside = cancel, no save (the ticket's decision; deliberately unlike
  // the Changes card, whose blur commits). Also treats focus moving into the
  // renderer iframe as outside.
  useOutsideAlerter(ref, cancel);
  // One translation editor at a time: opening the popover or the span editor
  // closes this, and vice versa.
  useExclusiveTranslationPopover(cancel);

  // Seed the text and place the caret where the click landed, then focus —
  // unless the renderer iframe holds focus (CLAUDE.md pitfall 10: stealing it
  // reverts an in-flight canvas edit).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.textContent = initialValue;
    if (document.activeElement instanceof HTMLIFrameElement) return;
    el.focus();
    const textNode = el.firstChild;
    const selection = window.getSelection();
    if (!textNode || !selection) return;
    const offset = Math.max(0, Math.min(caretOffset, initialValue.length));
    try {
      selection.collapse(textNode, offset);
    } catch {
      // A browser that refuses the range leaves the caret where focus put it.
    }
    // Mount-only by design: re-seeding on a prop change would wipe the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Native key trap (see the component doc). Enter during an IME composition
  // is the composition's own confirm, never a save.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancelRef.current();
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        if (e.isComposing || e.keyCode === 229) return;
        onCommitRef.current(draftRef.current);
        return;
      }
      e.stopPropagation();
    };
    const stop = (e: KeyboardEvent) => e.stopPropagation();
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('keyup', stop);
    el.addEventListener('keypress', stop);
    return () => {
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('keyup', stop);
      el.removeEventListener('keypress', stop);
    };
  }, []);

  // The insertion point, drawn as the transcript's own gold playhead bar
  // (`Word.cursor-before`) instead of the browser's hairline caret, so the
  // reviewer sees exactly where typing lands the same way they see where
  // playback is. Tracked from the live selection; the native caret is hidden.
  const [caretRect, setCaretRect] = useState<{
    left: number;
    top: number;
    height: number;
  } | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const selection = window.getSelection();
      if (
        !selection ||
        selection.rangeCount === 0 ||
        !selection.isCollapsed ||
        !el.contains(selection.anchorNode)
      ) {
        setCaretRect(null);
        return;
      }
      const range = selection.getRangeAt(0);
      // jsdom has no layout: no rect API on a Range, and no caret to draw.
      if (typeof range.getBoundingClientRect !== 'function') {
        setCaretRect(null);
        return;
      }
      let rect = range.getBoundingClientRect();
      if (rect.height === 0) rect = el.getBoundingClientRect();
      setCaretRect({ left: rect.left, top: rect.top, height: rect.height });
    };
    measure();
    document.addEventListener('selectionchange', measure);
    el.addEventListener('input', measure);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      document.removeEventListener('selectionchange', measure);
      el.removeEventListener('input', measure);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, []);

  const handlePaste = (e: React.ClipboardEvent<HTMLSpanElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain').replace(/\s+/g, ' ');
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(text));
    range.collapse(false);
    setDraft(ref.current?.textContent ?? '');
  };

  return (
    <>
      <EditableSpan
        ref={ref}
        role="textbox"
        aria-label="Edit translation"
        aria-multiline="false"
        contentEditable
        suppressContentEditableWarning
        spellCheck
        data-testid="inline-phrase-field"
        onInput={() => setDraft(ref.current?.textContent ?? '')}
        onPaste={handlePaste}
        onDrop={e => e.preventDefault()}
        // The phrase span around this seeks / opens menus on its own clicks;
        // none of that may run from inside the field.
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        onContextMenu={e => e.stopPropagation()}
      />
      {caretRect &&
        ReactDOM.createPortal(
          <Caret
            aria-hidden="true"
            data-testid="inline-phrase-caret"
            style={{
              left: caretRect.left - 1,
              top: caretRect.top,
              height: caretRect.height,
            }}
          />,
          document.body,
        )}
      <InlineEditRedlineBubble
        anchorElement={anchorElement}
        before={initialValue}
        draft={draft}
        canRevert={canRevert}
        onRevert={onRevert}
      />
    </>
  );
};

// Seamless by design: the phrase does not turn into a form control, it just
// becomes typeable where it sits. Same line box (inline, inherited font and
// leading, wraps like prose), the primary text colour so the words come one
// step forward, the page's own dark fill (the Changes card's field uses the
// same, so the two read as one family) and a hairline underneath as the only
// signs of an open edit — both drawn as shadows, so nothing gains width or moves — and the
// caret in the transcript's own gold so the eye finds it. No outline: the
// browser's focus ring would read as a form box.
const EditableSpan = styled.span`
  display: inline;
  font: inherit;
  line-height: inherit;
  white-space: pre-wrap;
  color: var(--text-primary);
  /* The caret is drawn by <Caret> below; the browser's hairline is hidden. */
  caret-color: transparent;
  cursor: text;
  outline: none;
  background: var(--background);
  box-shadow: inset 0 -1px 0 var(--focus-border);
  border-radius: 2px 2px 0 0;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
  padding: 0 1px;

  /* WebKit draws its own ring on a focused editable region and only stands
     down for an explicit rule on the focus states themselves. */
  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none !important;
    outline-style: none;
    box-shadow: inset 0 -1px 0 var(--focus-border);
  }
  -webkit-tap-highlight-color: transparent;

  /* Keep a caret box when everything has been deleted. The CSS escape is
     doubled because this is a JS template literal: a single backslash-2 is
     an (invalid) octal escape there, and the whole rule set was dropped. */
  &:empty::before {
    content: '\\200B';
  }
`;

// The playhead's own blink (Word.cursor-before in TranscriptionTextTranslated).
const caretBlink = keyframes`
  100% {
    opacity: 0;
  }
`;

// A 2px blinking bar marking where typing lands. It used to share its glyph
// with the transcript's playhead cursor; PLAT-11765 retired that cursor (the
// playing segment is marked by weight instead), so this is now the only caret
// in the review and the gold is unambiguous: it means "you are editing here".
// Body-portaled and fixed so it can sit at any point inside a wrapped inline
// run without disturbing the text.
const Caret = styled.span`
  position: fixed;
  width: 2px;
  border-radius: 1px;
  background-color: #dfb615;
  pointer-events: none;
  z-index: var(
    ${TOP_LAYER_VARS.contextMenu},
    ${CLIP_PREVIEW_Z.translationEditorPopover}
  );
  animation: ${caretBlink} steps(2) 1.5s infinite;
  will-change: opacity;
`;
